import { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { ICategorySeo } from '@features/categories';
import { TLocale, ISeo } from '@typescript/index';
import { IAppearanceObject } from '@features/pages/typescript';
import theme from '@styles/theme';
import InspirationListingHeader from '@components/InspirationListingHeader/InspirationListingHeader';
import RenderArticlesBlocks from '@features/cms/components/RenderArticlesBlock';
import { ICmsDocument, TCmsBlocks, ITagArticles, ICmsAllArticles, TInspirationsTypesSlug, IInteriorDesignFilter } from '@features/cms/typescript';
import getInspirationsListingProps from '@utils/pages/getInspirationsListingProps';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import MainSeo from '@components/MainSeo/MainSeo';
import getFilteredArticlesByTags from '@utils/pages/getFilteredArticlesByTags';
import getTagsFromUrl from '@utils/getTagsFromUrl';
import setUrlParam from '@utils/setUrlParam';
import WysiwygSeoBlock from '@components/WysiwygSeoBlock/WysiwygSeoBlock';
import { DEFAULT_LOCALE, PUBLIC_HOST } from '@config/index';
import { getCache } from '@utils/cacheHelpers';
import getFilteredArticlesByFilters from '@utils/pages/getFilteredArticlesByFilters';
import InteriorDesignsFilters from '@components/InteriorDesignsFilters/InteriorDesignsFilters';
import InteriorDesignsFiltersAside from '@components/InteriorDesignsFilters/InteriorDesignsFiltersAside';
import getFiltersArticlesFromUrl from '@utils/getFiltersArticlesFromUrl';
type PropsType = {
  content: Record<string, TCmsBlocks> | null;
  appearance: IAppearanceObject | undefined;
  seo: ISeo | undefined;
  allArticles: ICmsAllArticles;
  slug: TInspirationsTypesSlug;
  page: number;
  filters: IInteriorDesignFilter[] | null;
};
const ProjectsList = ({
  content: blocks,
  appearance,
  seo,
  allArticles,
  slug,
  page: initialPage,
  filters
}: PropsType) => {
  const {
    articles,
    articlesPremium
  } = allArticles;
  const [dynamicSeo, setDynamicSeo] = useState<ICategorySeo | undefined>(seo);
  const [usedTags, setUsedTags] = useState<Record<number, ITagArticles>>({});
  const [tags, setTags] = useState<ITagArticles[]>(() => articles?.baseData?.usedTags ? Object.values(articles.baseData.usedTags) : []);
  const [activeTags, setActiveTags] = useState<ITagArticles[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<ICmsDocument[]>([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [pageCount, setPageCount] = useState(initialPage);
  const [showFilters, setShowFilters] = useState(false);
  const router = useRouter();
  const background = appearance?.backgroundColor || theme.rawColors.beige300;
  const [filtersState, setFiltersState] = useState<IInteriorDesignFilter[]>(filters || []);
  const getCount = () => filteredArticles.length || filtersApplied ? filteredArticles.length : articles?.documents.length || 0;
  useEffect(() => {
    setFiltersState([...(filters || [])]);
  }, [filters]);
  const getActiveTagsId = (): number[] => {
    const activeTagsId: number[] = [];
    if (activeTags.length) {
      activeTags.forEach(tag => {
        const foundTagId = Object.keys(usedTags).find(tagId => usedTags[tagId] === tag);
        if (foundTagId) activeTagsId.push(+foundTagId);
      });
    }
    return activeTagsId;
  };
  const getFilteredArticles = () => {
    const activeTagsId = getActiveTagsId();
    let newfilteredArticles: ICmsDocument[] = [];
    let filtersApplied = false;
    if (activeTagsId.length) {
      const allArticlesToFilter = [...(articles?.documents || []), ...(articlesPremium?.documents || [])];
      newfilteredArticles = getFilteredArticlesByTags(allArticlesToFilter?.length ? allArticlesToFilter : [], activeTagsId);
      filtersApplied = true;
    }
    if (filtersState && filtersState.length && filtersState.filter(f => f.isChecked).length) {
      newfilteredArticles = getFilteredArticlesByFilters(newfilteredArticles?.length ? newfilteredArticles : articles?.documents || [], filtersState);
      filtersApplied = true;
    }
    setFiltersApplied(filtersApplied);
    setFilteredArticles(newfilteredArticles);
  };
  const handlePageCount = () => {
    setPageCount(pageCount + 1);
  };
  const renderCanonicalLink = useMemo(() => {
    const {
      asPath,
      locale
    } = router;
    let currentUrl;
    if (activeTags.length || pageCount) {
      currentUrl = `${PUBLIC_HOST}${locale !== DEFAULT_LOCALE ? `/${locale}${asPath}` : asPath}`;
    }
    return <SelfCanonicalLink currentUrl={currentUrl} />;
  }, [router.asPath]);

  // TODO: to improve after update document with tags
  useEffect(() => {
    if (articles) {
      setTags(Object.values(articles.baseData.usedTags));
      setUsedTags(articles.baseData.usedTags);
      getTagsFromUrl(Object.values(articles.baseData.usedTags), setActiveTags);
      getFiltersArticlesFromUrl(filtersState, setFiltersState);
    }
  }, [tags.length === 0]);
  useEffect(() => {
    if (activeTags.length || filters?.length) {
      getFilteredArticles();
      setUrlParam(router, activeTags, pageCount >= 2 ? pageCount : undefined, filtersState);
      if (activeTags.length && activeTags[0] && activeTags[0].metaTitle) {
        setDynamicSeo({
          title: activeTags[0].metaTitle,
          description: activeTags[0].metaDescription || seo?.description || ''
        });
      } else {
        setDynamicSeo(seo);
      }
    } else {
      setFilteredArticles([]);
      setUrlParam(router);
      setDynamicSeo(seo);
    }
  }, [activeTags.length, filtersState?.filter(filter => filter.isChecked).length, showFilters]);
  useEffect(() => {
    if (pageCount >= 2) {
      setUrlParam(router, activeTags.length ? activeTags : undefined, pageCount);
    } else {
      setUrlParam(router, activeTags.length ? activeTags : undefined);
    }
  }, [pageCount]);
  return <>
      {renderCanonicalLink}
      <main id="main-content" sx={{
      background,
      overflowX: 'hidden'
    }}>
        {seo && <MainSeo seo={dynamicSeo || seo} />}
        <InspirationListingHeader activeLink={slug} count={getCount()} tags={tags} activeTags={activeTags} setActiveTags={setActiveTags} setPageCount={setPageCount} />
        {filtersState && filtersState.length > 0 && <InteriorDesignsFilters projectsCount={getCount()} filters={filtersState} showFilters={setShowFilters} setFiltersState={setFiltersState} noResultsFound={filtersApplied && filteredArticles.length === 0} />}
        {blocks && articles && RenderArticlesBlocks(blocks, articles?.documents, articlesPremium?.documents, filteredArticles, true, true, background, handlePageCount, pageCount, {
        hits: [],
        sendEvent: () => null
      }, initialPage)}
        {activeTags[0] && activeTags[0].description && <WysiwygSeoBlock description={activeTags[0].description} />}
        {filtersState && filtersState.length > 0 && <InteriorDesignsFiltersAside asideActive={showFilters} filters={filtersState} setShowFilters={setShowFilters} setFiltersState={setFiltersState} />}
      </main>
    </>;
};
export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const {
    locale,
    resolvedUrl
  } = context;
  let page = 1;
  if (resolvedUrl.includes('?')) {
    const queryString = resolvedUrl.split('?')[1] || '';
    const queryParams = new URLSearchParams(queryString);
    page = parseInt(queryParams.get('page') || '1', 10);
  }
  const cacheKey = `Interior-designs-${page}-${locale}`;
  const cachePropsTime = process.env.INSPIRATIONS_PROPS_REVALIDATION_TIME ? parseInt(process.env.INSPIRATIONS_PROPS_REVALIDATION_TIME, 10) : 600;
  const inspirationListingProps = await getCache(cacheKey, () => getInspirationsListingProps(locale as TLocale, 'interior-designs', page), cachePropsTime, true);
  return inspirationListingProps;
};
export default ProjectsList;